import { CheckCircle, FolderPlus, Loader2, Search, Upload } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Folder } from "~/api/model";
import { Loader } from "~/components/Loader";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Progress } from "~/components/ui/progress";
import { MAX_FILE_SIZE_BYTES } from "~/features/settings/components/reports/importExportUtils";
import { useReportSampleUpload } from "~/features/settings/hooks/useReportSampleUpload";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useFtue } from "~/hooks/useFtue";
import { CreateCaseDialog } from "./CreateCaseDialog";

enum FtueStep {
  UPLOAD_REPORT = "upload_report",
  REPORT_SUCCESS = "report_success",
  CREATE_CASE = "create_case",
}

interface FtueWelcomeProps {
  demoCase?: Folder;
  isDemoCaseLoading?: boolean;
}

export const FtueWelcome: FC<FtueWelcomeProps> = ({
  demoCase,
  isDemoCaseLoading,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { user, completeFtue, isCompletingFtue, trackFtueEvent } = useFtue();
  const { toast } = useToast();
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [currentStep, setCurrentStep] = useState<FtueStep>(
    FtueStep.UPLOAD_REPORT
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadReportSample, isUploading, uploadProgress } =
    useReportSampleUpload({ organizationId });

  useEffect(() => {
    if (!hasTrackedView) {
      trackFtueEvent("ftue_welcome_viewed");
      setHasTrackedView(true);
    }
  }, [hasTrackedView, trackFtueEvent]);

  const firstName = user?.name?.split(" ")[0] || "";

  const handleExploreDemoCase = async () => {
    trackFtueEvent("ftue_explore_demo_clicked");
    await completeFtue("explore_demo");
    if (demoCase) {
      navigate(ROUTES.CASE({ organizationId, caseId: demoCase.id }));
    }
  };

  const handleSkip = async () => {
    trackFtueEvent("ftue_skipped");
    await completeFtue("skip");
  };

  const handleCreateCaseClick = () => {
    trackFtueEvent("ftue_create_case_clicked");
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "importTemplate.fileTooLarge",
          defaultMessage: "File size exceeds the maximum limit of 25MB",
        }),
      });
      return;
    }

    try {
      await uploadReportSample(file);
      setCurrentStep(FtueStep.REPORT_SUCCESS);
    } catch {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "ftue.uploadReport.error",
          defaultMessage: "Failed to upload report",
        }),
      });
    }
  };

  const handleSkipReportUpload = () => {
    setCurrentStep(FtueStep.CREATE_CASE);
  };

  // Step: Upload Report
  if (currentStep === FtueStep.UPLOAD_REPORT) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4">
        <div className="max-w-xl w-full text-center space-y-8">
          <div className="space-y-3">
            <h1 className="text-3xl font-semibold text-foreground">
              <FormattedMessage
                id="ftue.uploadReport.title"
                defaultMessage="Upload a Sample Report"
              />
            </h1>
            <p className="text-lg text-muted-foreground">
              <FormattedMessage
                id="ftue.uploadReport.subtitle"
                defaultMessage="Help Sky AI learn your preferred report format by uploading an example."
              />
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.md,.markdown,application/json"
            className="hidden"
            onChange={handleFileSelect}
          />

          <Card
            className={`cursor-pointer transition-all ${
              isUploading
                ? "border-blue-300 bg-blue-50/50 dark:border-blue-700 dark:bg-blue-950/50"
                : "hover:border-blue-500 hover:shadow-md"
            }`}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            <CardHeader className="text-center py-10">
              {isUploading ? (
                <>
                  <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                  </div>
                  <CardTitle className="text-lg text-blue-700 dark:text-blue-300">
                    <FormattedMessage
                      id="ftue.uploadReport.uploading"
                      defaultMessage="Uploading your report..."
                    />
                  </CardTitle>
                  <div className="mt-4 px-8">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-sm text-muted-foreground mt-2">
                      {uploadProgress}%
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Upload className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">
                    <FormattedMessage
                      id="ftue.uploadReport.dropzone"
                      defaultMessage="Click to upload a report"
                    />
                  </CardTitle>
                  <CardDescription className="mt-2">
                    <FormattedMessage
                      id="ftue.uploadReport.formats"
                      defaultMessage="Supports PDF/Markdown, JSON files (max 25MB)"
                    />
                  </CardDescription>
                </>
              )}
            </CardHeader>
          </Card>

          <div className="flex justify-center gap-4">
            <Button
              variant="link"
              onClick={handleSkipReportUpload}
              disabled={isUploading}
              className="text-muted-foreground hover:text-foreground"
            >
              <FormattedMessage
                id="ftue.uploadReport.skip"
                defaultMessage="Skip for now"
              />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Report Upload Success
  if (currentStep === FtueStep.REPORT_SUCCESS) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4">
        <div className="max-w-xl w-full text-center space-y-8">
          <div className="mx-auto w-20 h-20 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl font-semibold text-foreground">
              <FormattedMessage
                id="ftue.reportSuccess.title"
                defaultMessage="Report Uploaded Successfully!"
              />
            </h1>
            <p className="text-lg text-muted-foreground">
              <FormattedMessage
                id="ftue.reportSuccess.subtitle"
                defaultMessage="Sky AI will use this as a template for generating your reports."
              />
            </p>
          </div>

          <Button
            size="lg"
            onClick={() => setCurrentStep(FtueStep.CREATE_CASE)}
            className="px-8"
          >
            <FormattedMessage
              id="ftue.reportSuccess.continue"
              defaultMessage="Continue"
            />
          </Button>
        </div>
      </div>
    );
  }

  // Step: Create Case (after report upload)
  if (currentStep === FtueStep.CREATE_CASE) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4">
        <div className="max-w-2xl w-full text-center space-y-8">
          <div className="space-y-3">
            <h1 className="text-3xl font-semibold text-foreground">
              <FormattedMessage
                id="ftue.welcome.title"
                defaultMessage="Welcome to Sky AI, {firstName}!"
                values={{ firstName }}
              />
            </h1>
            <p className="text-lg text-muted-foreground">
              <FormattedMessage
                id="ftue.welcome.subtitle"
                defaultMessage="Get your first case organized in minutes."
              />
            </p>
          </div>

          <div className="flex items-center justify-center gap-4 md:flex-row flex-col">
            <CreateCaseDialog
              isFirstCase={true}
              onCaseCreated={() => completeFtue("create_case")}
              TriggerButton={
                <Card
                  className="md:w-[50%] w-full cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                  onClick={handleCreateCaseClick}
                >
                  <CardHeader className="text-center">
                    <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <FolderPlus className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">
                      <FormattedMessage
                        id="ftue.welcome.createCase"
                        defaultMessage="Create Your First Case"
                      />
                    </CardTitle>
                    <CardDescription className="mt-2">
                      <FormattedMessage
                        id="ftue.welcome.createCaseDescription"
                        defaultMessage="Upload documents and let Sky AI organize them for you."
                      />
                    </CardDescription>
                  </CardHeader>
                </Card>
              }
            />

            {demoCase ? (
              <Card
                className="md:w-[50%] w-full cursor-pointer hover:border-purple-500 hover:shadow-md transition-all"
                onClick={handleExploreDemoCase}
              >
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                    <Search className="w-6 h-6 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg">
                    <FormattedMessage
                      id="ftue.welcome.exploreDemo"
                      defaultMessage="Explore Demo Case"
                    />
                  </CardTitle>
                  <CardDescription>
                    <FormattedMessage
                      id="ftue.welcome.exploreDemoDescription"
                      defaultMessage="See how Sky AI works with sample documents."
                    />
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : isDemoCaseLoading ? (
              <Card className="md:w-[50%] w-full border-dashed border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-950/50">
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                    <Loader size={24} color="#9333ea" thickness={2} />
                  </div>
                  <CardTitle className="text-lg text-purple-700 dark:text-purple-300">
                    <FormattedMessage
                      id="ftue.welcome.demoCaseLoading"
                      defaultMessage="Setting up your demo case..."
                    />
                  </CardTitle>
                  <CardDescription>
                    <FormattedMessage
                      id="ftue.welcome.demoCaseLoadingDescription"
                      defaultMessage="This will only take a moment."
                    />
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : null}
          </div>

          {demoCase && (
            <div>
              <Button
                variant="link"
                onClick={handleSkip}
                disabled={isCompletingFtue}
                className="text-muted-foreground hover:text-foreground"
              >
                <FormattedMessage
                  id="ftue.welcome.skip"
                  defaultMessage="Skip to dashboard"
                />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
};
